/**
 * Reusable financial blocks: KPI summary and income/expense chart with a
 * monthly/quarterly/yearly toggle. Used by the dashboard, property and reports pages.
 */
import type { BucketTotals, Summary } from "@shared/finance/ledger.ts";
import type { Granularity } from "@shared/periods.ts";
import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import type { Params } from "../lib/api.ts";
import { withQuery } from "../lib/api.ts";
import { useApi } from "../lib/query.ts";
import { IncomeExpenseChart } from "./charts/Charts.tsx";
import { Money, QueryState, Section, StatCard } from "./ui/kit.tsx";

export const GRANULARITIES: Array<{ value: Granularity; label: string }> = [
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "year", label: "Yearly" },
  { value: "tax_year", label: "Tax year" },
];

export function SummaryCards({
  summary,
  scope,
  extra,
}: {
  summary: Summary;
  scope: Params;
  extra?: React.ReactNode;
}) {
  const link = (line: string) => withQuery("/reports/transactions", { ...scope, line });
  return (
    <Row className="g-3 mb-4">
      <Col sm={6} xl={3}>
        <StatCard
          label="Income"
          value={<Money pence={summary.incomePence} kind="income" />}
          tone="income"
          to={link("income")}
          icon="income"
        />
      </Col>
      <Col sm={6} xl={3}>
        <StatCard
          label="Expenses"
          value={<Money pence={summary.expensesPence} kind="expense" />}
          tone="expense"
          to={withQuery("/reports/transactions", { ...scope, kind: "expense" })}
          icon="expense"
          hint={
            summary.financeCostsPence ? (
              <>
                incl. finance costs <Money pence={summary.financeCostsPence} />
              </>
            ) : undefined
          }
        />
      </Col>
      <Col sm={6} xl={3}>
        <StatCard
          label="Net profit / loss"
          value={<Money pence={summary.netPence} kind="net" />}
          tone="net"
          to={withQuery("/reports/pnl", scope)}
          icon="chart"
        />
      </Col>
      {extra ?? (
        <Col sm={6} xl={3}>
          <StatCard
            label="Capital expenditure"
            value={<Money pence={summary.capitalPence} />}
            hint="Not included in profit"
            to={link("capital")}
          />
        </Col>
      )}
    </Row>
  );
}

export function ChartPanel({ scope, title = "Income vs expenses" }: { scope: Params; title?: string }) {
  const [granularity, setGranularity] = useState<Granularity>("month");
  const [show, setShow] = useState<"all" | "income" | "expenses" | "net">("all");
  const q = useApi<{ buckets: BucketTotals[] }>(
    "/api/reports/timeseries",
    { ...scope, granularity },
    { keepPrevious: true },
  );
  return (
    <Section
      title={title}
      actions={
        <>
          <select
            className="form-select form-select-sm"
            aria-label="Series"
            value={show}
            onChange={(e) => setShow(e.target.value as typeof show)}
          >
            <option value="all">Income vs expenses</option>
            <option value="income">Income over time</option>
            <option value="expenses">Expenses over time</option>
            <option value="net">Profit / loss over time</option>
          </select>
          <select
            className="form-select form-select-sm"
            aria-label="Group by"
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as Granularity)}
          >
            {GRANULARITIES.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </>
      }
    >
      <QueryState query={q}>{(d) => <IncomeExpenseChart buckets={d.buckets} show={show} />}</QueryState>
    </Section>
  );
}
